import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { resolvePostAuthPath } from "../lib/navigationState";
import {
  PASSWORD_INPUT_TOGGLE_CLASS,
  PASSWORD_TOGGLE_BTN_CLASS,
  showsPasswordToggle,
  usePasswordToggleVisibility,
} from "../lib/passwordFieldToggle";

const THANK_YOU_TEXT = "Thank you for being here.";
const SETUP_STEP_DELAY_MS = 1400;

const PREVIEW_CONTEXT = {
  user: { name: "Maya Chen", email: "hello@aurorastudio.co" },
  client: { name: "Aurora Studio" },
};

function firstName(name) {
  const cleaned = (name || "").trim();
  if (!cleaned) return "there";
  return cleaned.split(/\s+/)[0];
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

function OnboardingWelcomeStep({ greetingName, onThankYouComplete }) {
  const reducedMotion = usePrefersReducedMotion();
  const [visibleLen, setVisibleLen] = useState(reducedMotion ? THANK_YOU_TEXT.length : 0);

  useEffect(() => {
    if (reducedMotion) {
      setVisibleLen(THANK_YOU_TEXT.length);
      return undefined;
    }

    setVisibleLen(0);
    const len = THANK_YOU_TEXT.length;
    const stepMs = Math.max(26, Math.min(72, Math.floor(3400 / len)));
    const durationMs = stepMs * len;
    let rafId = 0;
    let startTs = null;
    let lastEmitted = -1;

    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const elapsed = ts - startTs;
      const progress = Math.min(1, elapsed / durationMs);
      const next = progress >= 1 ? len : Math.floor(progress * len);
      if (next !== lastEmitted) {
        lastEmitted = next;
        setVisibleLen(next);
      }
      if (next < len) rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [reducedMotion]);

  useEffect(() => {
    if (visibleLen < THANK_YOU_TEXT.length || !onThankYouComplete) return undefined;
    const id = window.setTimeout(onThankYouComplete, reducedMotion ? 0 : SETUP_STEP_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [visibleLen, onThankYouComplete, reducedMotion]);

  return (
    <div
      className="flex min-h-screen w-full flex-col items-center justify-center px-4 text-center"
      data-testid="onboarding-step-welcome"
    >
      <h2 className="font-heading mb-3 text-2xl tracking-tight md:text-3xl">Welcome, {greetingName}.</h2>
      <p className="text-sm leading-snug text-[var(--t-meta)] md:leading-relaxed">
        {THANK_YOU_TEXT.slice(0, visibleLen)}
        {!reducedMotion && visibleLen < THANK_YOU_TEXT.length ? (
          <span
            className="inline-block ml-1 h-[0.82em] w-[2px] align-[-0.06em] bg-current motion-safe:animate-welcome-caret"
            aria-hidden
          />
        ) : null}
      </p>
    </div>
  );
}

export default function OnboardingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const preview = params.get("preview") === "1";
  const onboardingToken = params.get("token") || "";
  const previewStep = params.get("step") === "setup";

  const [context, setContext] = useState(preview ? PREVIEW_CONTEXT : null);
  const [loading, setLoading] = useState(!preview);
  const [step, setStep] = useState(preview && previewStep ? "setup" : "welcome");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (preview) return;
    if (!onboardingToken) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/onboarding/context", {
          params: { token: onboardingToken },
        });
        setContext(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "This setup link is not valid.");
        navigate("/", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [preview, onboardingToken, navigate]);

  const showSetupStep = useCallback(() => {
    setStep("setup");
  }, []);

  const greetingName = useMemo(() => firstName(context?.user?.name), [context?.user?.name]);
  const canSubmit = password.length >= 8 && password === confirmPassword;
  const showOnboardingSignIn = showsPasswordToggle(password);
  const passwordsMatch = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;
  const passwordToggleVisible = usePasswordToggleVisibility(password);
  const confirmPasswordToggleVisible = usePasswordToggleVisibility(confirmPassword);

  useEffect(() => {
    if (!passwordToggleVisible) setShowPassword(false);
  }, [passwordToggleVisible]);

  useEffect(() => {
    if (!confirmPasswordToggleVisible) setShowConfirmPassword(false);
  }, [confirmPasswordToggleVisible]);

  const completePassword = async () => {
    if (preview) {
      toast.message("Preview only — finish setup is disabled.");
      return;
    }
    if (!canSubmit || !onboardingToken) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/onboarding/password", {
        onboarding_token: onboardingToken,
        password,
      });
      login(data.token, data.user, data.client);
      navigate(resolvePostAuthPath(data.user, data.client), { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not finish setup");
    } finally {
      setBusy(false);
    }
  };

  const startGoogle = async () => {
    if (preview) {
      toast.message("Preview only — Google sign-in is disabled.");
      return;
    }
    if (!onboardingToken) {
      toast.error("Missing setup token");
      return;
    }
    setGoogleBusy(true);
    try {
      const { data } = await api.get("/auth/google/login-url", {
        params: { onboarding_token: onboardingToken },
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sign-in is not available");
      setGoogleBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="onboarding-page">
        <div className="t-meta">Preparing your workspace…</div>
      </div>
    );
  }

  if (!preview && !onboardingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="onboarding-page">
        <div className="max-w-md text-center">
          <div className="label-eyebrow mb-4">TAĪSTU</div>
          <h1 className="font-heading text-3xl mb-3 tracking-tight">Setup link missing.</h1>
          <p className="t-meta mb-6">Open the invite link from your email to finish portal setup.</p>
          <button type="button" onClick={() => navigate("/")} className="t-btn-primary" data-testid="onboarding-back-login">
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-0 flex flex-col items-center justify-center overflow-hidden bg-[var(--t-bg)] px-4 py-4 md:px-10 md:py-6"
      data-testid="onboarding-page"
    >
      {step === "welcome" ? (
        <OnboardingWelcomeStep greetingName={greetingName} onThankYouComplete={showSetupStep} />
      ) : (
        <div key="onboarding-setup" className="flex max-h-full min-h-0 w-full max-w-md flex-col justify-center t-fade-up">
          <div className="min-h-0 shrink rounded-[11px] border border-[var(--t-border)] bg-[var(--t-bg)] p-8 shadow-sm md:p-10">
            <div data-testid="onboarding-step-setup">
              <p className="mb-1 font-heading text-sm leading-snug tracking-tight !font-medium text-[var(--t-text)] md:leading-relaxed">
                Finish setting up.
              </p>
              <p className="mb-6 text-sm leading-snug text-[var(--t-meta)] md:leading-relaxed">
                Create a password for Home or continue with Google using the same email as your invite.
              </p>

              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow block">Password</label>
                {showOnboardingSignIn ? (
                  <button
                    type="button"
                    onClick={completePassword}
                    disabled={!passwordsMatch || busy}
                    className="text-[var(--t-accent)] text-xs tracking-widest uppercase hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    data-testid="onboarding-password-signin"
                  >
                    {busy ? "Signing in…" : "Sign in"}
                  </button>
                ) : null}
              </div>
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  className={PASSWORD_INPUT_TOGGLE_CLASS}
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPassword(next);
                    if (!showsPasswordToggle(next)) setShowPassword(false);
                  }}
                  autoComplete="new-password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      completePassword();
                    }
                  }}
                  data-testid="onboarding-password-input"
                />
                {passwordToggleVisible ? (
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className={PASSWORD_TOGGLE_BTN_CLASS}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="onboarding-toggle-password"
                  >
                    {showPassword ? <EyeOff size={16} strokeWidth={1.6} /> : <Eye size={16} strokeWidth={1.6} />}
                  </button>
                ) : null}
              </div>

              <label className="label-eyebrow mb-1 block">Confirm password</label>
              <div className="relative mb-4">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={PASSWORD_INPUT_TOGGLE_CLASS}
                  placeholder="Repeat password"
                  value={confirmPassword}
                  onChange={(e) => {
                    const next = e.target.value;
                    setConfirmPassword(next);
                    if (!showsPasswordToggle(next)) setShowConfirmPassword(false);
                  }}
                  autoComplete="new-password"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      completePassword();
                    }
                  }}
                  data-testid="onboarding-confirm-password-input"
                />
                {confirmPasswordToggleVisible ? (
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className={PASSWORD_TOGGLE_BTN_CLASS}
                    aria-label={showConfirmPassword ? "Hide password confirmation" : "Show password confirmation"}
                    data-testid="onboarding-toggle-confirm-password"
                  >
                    {showConfirmPassword ? <EyeOff size={16} strokeWidth={1.6} /> : <Eye size={16} strokeWidth={1.6} />}
                  </button>
                ) : null}
              </div>

              {password && confirmPassword && password !== confirmPassword ? (
                <p className="mb-4 text-xs text-[var(--t-error)]" data-testid="onboarding-password-mismatch">
                  Passwords do not match.
                </p>
              ) : null}

              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-[var(--t-border)]" />
                <span className="label-eyebrow">or</span>
                <div className="h-px flex-1 bg-[var(--t-border)]" />
              </div>

              <button
                type="button"
                className="t-btn-primary flex w-full items-center justify-center gap-2"
                onClick={startGoogle}
                disabled={googleBusy}
                data-testid="onboarding-google-btn"
              >
                {googleBusy ? "Connecting…" : <>Sign in w/ Google <ArrowRight size={16} strokeWidth={1.4} /></>}
              </button>

              {preview ? (
                <p className="mt-5 text-center text-xs text-[var(--t-meta)]" data-testid="onboarding-preview-note">
                  Preview mode — actions are disabled.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
